/**
 * @file Restricted leave detail modal owner for the Shared Calendar domain.
 */

import {
    doc,
    getDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";

import { db } from "../../app/firebase/firebase.js";
import { appData, state } from "../../app/state/state.js";
import {
    closeForm,
    configureDefaultFormActions,
    formModalElement,
} from "../../shared/forms/modalShell.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import {
    escapeHTML,
    formatAndSanitizeUserContent,
} from "../../shared/utils/contentUtils.js";
import {
    isDrugsAndLeaveActionLocked,
} from "../../shared/utils/actionLock.js";
import {
    formatLeaveApprovedBy,
    formatLeaveApprovedOn,
    formatLeaveApprovalEvents,
    formatLeaveDateRange,
    formatLeaveOverlapDateLabel,
    formatLeaveSubmittedAt,
} from "./leaveDetailFormatting.js";
import {
    handleLeaveApprovalAction,
    revalidateApprovalOverlaps,
    requestLeaveCancellation,
} from "./calendarApi.js";
import { showCalendarErrorModal } from "./calendarModalFeedback.js";
import { openLeaveRequestModal } from "./leaveRequestModal.js";

const ACTIVE_APPROVAL_STATUSES = new Set(["pending", "escalated"]);
const ACTION_LOCK_MESSAGE = "Leave actions are temporarily locked for maintenance.";

function getCurrentUserId() {
    return state.currentUser?.uid || state.currentUserProfile?.id || "";
}

function userHasCalendarAccess(user) {
    return user?.role === "admin" ||
        (Array.isArray(user?.permissions) && user.permissions.includes("calendar"));
}

function canManageLeaveForOthers() {
    const detailTeamId = appData.config?.leave?.detailTeamId || "";
    const currentUser = state.currentUserProfile || {};
    const currentUserId = getCurrentUserId();
    const isTeamDocMember = (appData.teams || []).some((team) =>
        team?.id === detailTeamId &&
        Array.isArray(team.members) &&
        team.members.includes(currentUserId));
    return Boolean(
        detailTeamId &&
        currentUser.isActive !== false &&
        userHasCalendarAccess(currentUser) &&
        isTeamDocMember,
    );
}

function getUserName(userId) {
    const user = (appData.users || []).find((entry) => entry.id === userId);
    return user?.name || user?.email || userId || "Unknown user";
}

function getLeaveTypeName(leaveTypeId) {
    const type = (appData.config?.leave?.types || [])
        .find((entry) => entry.id === leaveTypeId);
    return type?.name || leaveTypeId || "Leave";
}

async function fetchLeaveData(leaveRequestId) {
    if (!leaveRequestId) {return null;}
    const snapshot = await getDoc(doc(db, "leave_requests", leaveRequestId));
    return snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null;
}

function getDetailRow(label, value) {
    return `
        <div>
            <dt class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                ${escapeHTML(label)}
            </dt>
            <dd class="mt-1 text-sm text-gray-900">${value}</dd>
        </div>
    `;
}

function renderOverlapWarnings(warnings = []) {
    if (!Array.isArray(warnings) || warnings.length === 0) {return "";}
    return `
        <div data-calendar-overlap-warnings>
            <h5 class="text-sm font-semibold text-gray-700">Overlaps</h5>
            <ul class="mt-2 space-y-2 text-sm text-gray-700">
                ${warnings.map((warning) => `
                    <li class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                        <strong>${escapeHTML(warning.userName || "Unknown user")}</strong>
                        <span class="block text-xs text-gray-500">
                            ${escapeHTML(formatLeaveOverlapDateLabel(warning))}
                        </span>
                    </li>
                `).join("")}
            </ul>
        </div>
    `;
}

function renderApprovalOverlapWarningContainer(warnings = []) {
    return `
        <div data-calendar-overlap-warning-container>
            ${renderOverlapWarnings(warnings)}
        </div>
    `;
}

function renderApprovalEvents(leaveData = {}) {
    const events = formatLeaveApprovalEvents(leaveData, getUserName);
    if (events.length === 0) {return "";}
    return `
        <div data-calendar-leave-approval-events>
            <h5 class="text-sm font-semibold text-gray-700">Approval History</h5>
            <ul class="mt-2 space-y-2 text-sm text-gray-700">
                ${events.map((event) => `
                    <li class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                        <span class="font-medium text-gray-900">
                            ${escapeHTML(event.label)}
                        </span>
                        <span class="block text-xs text-gray-500">
                            ${escapeHTML(event.actorName)}
                            ${event.at ? ` &middot; ${escapeHTML(event.at)}` : ""}
                        </span>
                    </li>
                `).join("")}
            </ul>
        </div>
    `;
}

function renderLeaveDetail(leaveData, approvalData = null) {
    const data = leaveData || approvalData?.payload || {};
    const showApprovalOutcome = !approvalData;
    return `
        <div class="space-y-4" data-calendar-leave-detail>
            <div>
                <h4 class="text-lg font-semibold text-gray-900">
                    ${escapeHTML(data.requesterName || getUserName(data.requesterId))}
                </h4>
                <p class="mt-1 text-sm text-gray-500">
                    ${escapeHTML(formatLeaveDateRange(data))}
                </p>
            </div>
            <dl class="grid grid-cols-1 gap-4 md:grid-cols-2">
                ${getDetailRow("Leave Type", escapeHTML(getLeaveTypeName(data.leaveTypeId)))}
                ${getDetailRow("Submitted", escapeHTML(formatLeaveSubmittedAt(data)))}
                ${showApprovalOutcome ? `
                    ${getDetailRow("Approved By", escapeHTML(formatLeaveApprovedBy(data, getUserName)))}
                    ${getDetailRow("Approved On", escapeHTML(formatLeaveApprovedOn(data)))}
                ` : ""}
            </dl>
            ${data.details ? `
                <div>
                    <h5 class="text-sm font-semibold text-gray-700">Details</h5>
                    <div class="mt-2 rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                        ${formatAndSanitizeUserContent(data.details)}
                    </div>
                </div>
            ` : ""}
            ${approvalData ? renderApprovalOverlapWarningContainer(
                approvalData?.payload?.overlapWarnings || [],
            ) : ""}
            ${showApprovalOutcome ? renderApprovalEvents(data) : ""}
            ${approvalData?.rejectionComment ? `
                <div>
                    <h5 class="text-sm font-semibold text-gray-700">Rejection Reason</h5>
                    <p class="mt-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
                        ${escapeHTML(approvalData.rejectionComment)}
                    </p>
                </div>
            ` : ""}
        </div>
    `;
}

function canChangeLeave(leaveData) {
    if (!leaveData || leaveData.status !== "approved") {return false;}
    if (leaveData.requestedAction === "cancel") {return false;}
    const currentUserId = getCurrentUserId();
    if (leaveData.requesterId === currentUserId) {return true;}
    return canManageLeaveForOthers();
}

function createPreviewActionButton({
    label,
    icon,
    variant = "brand",
    onClick,
    disabled = false,
}) {
    const button = document.createElement("button");
    button.type = "button";
    button.setAttribute("aria-label", label);
    button.title = disabled ? ACTION_LOCK_MESSAGE : label;
    button.disabled = disabled;
    button.dataset.modalCustomActions = "true";
    button.className = [
        "inline-flex h-8 w-8 items-center justify-center rounded-full bg-transparent",
        "text-gray-500 transition-colors focus:outline-none focus-visible:ring-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        variant === "danger" ?
            "hover:text-red-600 focus-visible:ring-red-200" :
            "hover:text-[var(--dfc-brand-primary)] focus-visible:ring-[var(--dfc-brand-primary-ring)]",
    ].join(" ");
    if (!disabled) {
        button.addEventListener("click", onClick);
    }

    const iconElement = document.createElement("span");
    iconElement.className = "material-symbols-outlined text-[20px] leading-none";
    iconElement.setAttribute("aria-hidden", "true");
    iconElement.textContent = icon;
    button.append(iconElement);
    return button;
}

function configurePreviewActions(leaveData) {
    const cancelBtn = document.getElementById("cancel-form-btn");
    const submitBtn = formModalElement?.querySelector("button[type=\"submit\"]");
    const footer = cancelBtn?.parentElement || submitBtn?.parentElement;
    const formHeaderActions = formModalElement?.querySelector("#form-header-actions");
    if (!cancelBtn || !submitBtn || !footer || !formHeaderActions) {return;}

    formHeaderActions.innerHTML = "";
    footer.style.display = "none";
    submitBtn.style.display = "none";
    cancelBtn.style.display = "none";

    const actionLocked = isDrugsAndLeaveActionLocked();
    if (canChangeLeave(leaveData)) {
        const cancelLeaveButton = createPreviewActionButton({
            label: "Cancel Leave",
            icon: "delete",
            variant: "danger",
            onClick: () => openLeaveCancellationConfirmation(leaveData),
            disabled: actionLocked,
        });
        const editButton = createPreviewActionButton({
            label: "Edit Leave",
            icon: "edit",
            onClick: () => {
                closeForm();
                openLeaveRequestModal(leaveData);
            },
            disabled: actionLocked,
        });
        formHeaderActions.append(cancelLeaveButton, editButton);
    }

    const closeButton = createPreviewActionButton({
        label: "Close Leave Details",
        icon: "close",
        variant: "danger",
        onClick: closeForm,
    });
    formHeaderActions.append(closeButton);
}

function openLeaveCancellationConfirmation(leaveData) {
    if (isDrugsAndLeaveActionLocked()) {
        showCalendarErrorModal(
            "Leave Action Locked",
            null,
            ACTION_LOCK_MESSAGE,
        );
        return;
    }
    openConfirmationModal(
        "Cancel Leave",
        `Request cancellation for <strong>${escapeHTML(formatLeaveDateRange(leaveData))}</strong>?`,
        async () => {
            await requestLeaveCancellation(leaveData.id, {});
            closeForm();
        },
        true,
        "Request Cancellation",
        null,
        "Submitting...",
        "Back",
    );
}

function showLeaveAccessDenied() {
    openConfirmationModal(
        "Leave Details Unavailable",
        "You do not have access to view this leave request.",
        () => {},
        false,
        "OK",
    );
}

function renderModalShell(title, html) {
    if (!formModalElement) {
        console.error("Form modal element (#form-modal) not found.");
        return false;
    }
    configureDefaultFormActions();
    const formTitle = formModalElement.querySelector("#form-title");
    const formHeaderActions = formModalElement.querySelector("#form-header-actions");
    const formFields = formModalElement.querySelector("#form-fields");
    const itemForm = formModalElement.querySelector("#item-form");
    if (!formTitle || !formHeaderActions || !formFields || !itemForm) {
        console.error("Required elements missing within #form-modal.");
        return false;
    }

    formTitle.textContent = title;
    formHeaderActions.innerHTML = "";
    itemForm.onsubmit = (event) => event.preventDefault();
    formFields.innerHTML = html;
    formModalElement.classList.add("flex");
    return true;
}

/**
 * Opens restricted leave detail.
 * @param {object} entry Display-safe calendar entry.
 * @returns {void}
 */
export function openLeaveDetailModal(entry) {
    const opened = renderModalShell(
        "Leave Details",
        `<p class="text-sm text-gray-500">Loading leave details...</p>`,
    );
    if (!opened) {return;}
    configurePreviewActions(null);

    fetchLeaveData(entry?.sourceId)
        .then((leaveData) => {
            if (!leaveData) {
                closeForm();
                showLeaveAccessDenied();
                return;
            }
            const formFields = formModalElement.querySelector("#form-fields");
            formFields.innerHTML = renderLeaveDetail(leaveData);
            configurePreviewActions(leaveData);
        })
        .catch((error) => {
            console.error("Error loading leave detail:", error);
            closeForm();
            showLeaveAccessDenied();
        });
}

function getRejectReasonHtml() {
    return `
        <div class="space-y-3 text-left">
            <p class="text-sm text-gray-600">
                Add the explanation for rejecting this leave request.
            </p>
            <textarea
                id="leave-approval-reject-reason"
                rows="3"
                class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                aria-label="Rejection reason"
            ></textarea>
        </div>
    `;
}

function openRejectApprovalModal(approvalId, onSuccess) {
    if (isDrugsAndLeaveActionLocked()) {
        showCalendarErrorModal(
            "Leave Action Locked",
            null,
            ACTION_LOCK_MESSAGE,
        );
        return;
    }
    openConfirmationModal(
        "Reject Leave",
        getRejectReasonHtml(),
        async () => {
            const reason = document
                .getElementById("leave-approval-reject-reason")
                ?.value
                ?.trim();
            if (!reason) {return false;}
            await handleLeaveApprovalAction(approvalId, "reject", reason);
            if (onSuccess) {onSuccess();}
            return true;
        },
        true,
        "Reject",
        null,
        "Rejecting...",
        "Back",
        { keepOpenOnFalse: true, closeOnError: false },
    );
}

function configureApprovalFooter(approvalId, approvalData) {
    const cancelBtn = document.getElementById("cancel-form-btn");
    const submitBtn = formModalElement?.querySelector("button[type=\"submit\"]");
    const footer = cancelBtn?.parentElement || submitBtn?.parentElement;
    if (!cancelBtn || !submitBtn || !footer) {return;}

    cancelBtn.textContent = "Close";
    submitBtn.style.display = "none";
    footer.className = "mt-6 flex items-center justify-between gap-3 px-2 pt-3 shrink-0";
    footer.querySelectorAll("[data-modal-custom-actions=\"true\"]")
        .forEach((element) => element.remove());
    cancelBtn.className = [
        "rounded-md bg-gray-200 px-3 py-1.5 text-sm font-semibold",
        "text-gray-800 transition-colors hover:bg-gray-300",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300",
        "disabled:cursor-not-allowed disabled:opacity-50",
    ].join(" ");

    if (!ACTIVE_APPROVAL_STATUSES.has(approvalData?.status)) {return;}

    const actionLocked = isDrugsAndLeaveActionLocked();
    const actionGroup = document.createElement("div");
    actionGroup.dataset.modalCustomActions = "true";
    actionGroup.className = "flex items-center gap-2";

    const approveButton = document.createElement("button");
    approveButton.type = "button";
    approveButton.textContent = "Approve";
    approveButton.disabled = actionLocked;
    approveButton.title = actionLocked ? ACTION_LOCK_MESSAGE : "";
    approveButton.className = [
        "rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold",
        "text-white hover:bg-green-700",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-green-300",
        "disabled:cursor-not-allowed disabled:opacity-50",
    ].join(" ");

    approveButton.addEventListener("click", async () => {
        if (actionLocked) {return;}
        if (actionGroup.dataset.processing === "true") {return;}
        setApprovalFooterProcessing(actionGroup, true, approveButton);
        try {
            const result = await handleLeaveApprovalAction(approvalId, "approve");
            if (result?.success === false) {
                setApprovalFooterProcessing(actionGroup, false);
                showCalendarErrorModal(
                    "Approval Failed",
                    null,
                    "Leave approval could not be completed.",
                );
                return;
            }
            closeForm();
        } catch (error) {
            console.error("Error approving leave request:", error);
            setApprovalFooterProcessing(actionGroup, false);
            showCalendarErrorModal(
                "Approval Failed",
                error,
                "Leave approval could not be completed.",
            );
        }
    });

    const rejectButton = document.createElement("button");
    rejectButton.type = "button";
    rejectButton.textContent = "Reject";
    rejectButton.disabled = actionLocked;
    rejectButton.title = actionLocked ? ACTION_LOCK_MESSAGE : "";
    rejectButton.className = [
        "rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold",
        "text-white hover:bg-red-700",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-red-300",
        "disabled:cursor-not-allowed disabled:opacity-50",
    ].join(" ");
    rejectButton.addEventListener("click", () => {
        if (actionLocked) {return;}
        if (actionGroup.dataset.processing === "true") {return;}
        openRejectApprovalModal(approvalId, closeForm);
    });

    actionGroup.append(rejectButton, approveButton);
    footer.insertBefore(actionGroup, cancelBtn.nextSibling);
}

async function refreshApprovalOverlapWarnings(approvalId, approvalData) {
    const container = formModalElement
        ?.querySelector("[data-calendar-overlap-warning-container]");
    if (!container || !approvalId || !approvalData) {return;}

    try {
        const result = await revalidateApprovalOverlaps(approvalId);
        const currentWarnings = result?.overlapWarnings || [];
        const currentContainer = formModalElement
            ?.querySelector("[data-calendar-overlap-warning-container]");
        if (!currentContainer) {return;}
        currentContainer.innerHTML = renderOverlapWarnings(currentWarnings);
    } catch (error) {
        console.warn("Leave approval overlap revalidation failed:", error);
    }
}

function setApprovalFooterProcessing(actionGroup, isProcessing, activeButton = null) {
    actionGroup.dataset.processing = isProcessing ? "true" : "false";
    const buttons = [
        document.getElementById("cancel-form-btn"),
        ...actionGroup.querySelectorAll("button"),
    ].filter(Boolean);
    buttons.forEach((button) => {
        if (!button.dataset.defaultText) {
            button.dataset.defaultText = button.textContent || "";
        }
        button.disabled = isProcessing;
        button.classList.toggle("opacity-60", isProcessing);
        button.classList.toggle("cursor-not-allowed", isProcessing);
        button.textContent = button.dataset.defaultText;
    });
    if (isProcessing && activeButton) {
        activeButton.textContent = "Approving...";
    }
}

/**
 * Opens the Leave approval modal from approval notifications.
 * @param {string} approvalId Approval id.
 * @param {object} approvalData Approval data.
 * @returns {void}
 */
export function openLeaveApprovalModal(approvalId, approvalData) {
    const payload = approvalData?.payload || {};
    renderModalShell(
        "Review Leave Request",
        renderLeaveDetail(payload, approvalData),
    );
    configureApprovalFooter(approvalId, approvalData);
    refreshApprovalOverlapWarnings(approvalId, approvalData);
}
